import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  ArrowLeft, Gift, Wine, Utensils, Sparkles, ShoppingBag, 
  Plane, Ticket, PartyPopper, Heart, Package, User, 
  Lock, Eye, EyeOff, CreditCard, Check, AlertCircle,
  Wallet, Send, ChevronRight, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Gift categories with icons
const CATEGORY_ICONS = {
  drinks: Wine,
  dinner: Utensils,
  spa_wellness: Sparkles,
  shopping: ShoppingBag,
  travel: Plane,
  entertainment: Ticket,
  celebration: PartyPopper,
  just_because: Heart,
  custom: Gift,
  flexible: Package
};

// Privacy options
const PRIVACY_OPTIONS = [
  { id: 'visible', label: 'Show My Name', description: 'Recipient sees who sent the gift immediately', icon: Eye },
  { id: 'revealed_on_redemption', label: 'Reveal on Use', description: 'Identity shown when gift is first used', icon: EyeOff },
  { id: 'anonymous', label: 'Stay Anonymous', description: 'Your identity remains private', icon: Lock }
];

// Preset amounts in cents
const PRESET_AMOUNTS = [
  { cents: 2500, label: '$25' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
  { cents: 25000, label: '$250' },
  { cents: 50000, label: '$500' },
  { cents: 100000, label: '$1,000' },
  { cents: 250000, label: '$2,500' },
  { cents: 500000, label: '$5,000' }
];

// Stripe Payment Form Component
const StripePaymentForm = ({ 
  giftId, 
  totalCents, 
  onSuccess, 
  onError,
  colors,
  isDark 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/gift-success?gift_id=${giftId}`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message);
        onError(submitError.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded without redirect
        onSuccess(paymentIntent.id);
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // 3D Secure or other action required - Stripe will handle redirect
        setError('Additional verification required. Please complete the verification.');
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <PaymentElement 
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                name: '',
              }
            }
          }}
        />
      </div>

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <AlertCircle size={18} color="#DC3545" />
          <span style={{ color: '#DC3545', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        data-testid="stripe-pay-btn"
        style={{
          width: '100%',
          padding: '16px',
          borderRadius: '12px',
          border: 'none',
          background: processing ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') : 'linear-gradient(135deg, #D4AF37, #A57C33)',
          color: processing ? colors.textMuted : '#1A1A1A',
          fontSize: '16px',
          fontWeight: '600',
          cursor: processing ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        {processing ? (
          <>
            <Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
            Processing Payment...
          </>
        ) : (
          <>
            <Lock size={18} />
            Pay ${(totalCents / 100).toFixed(2)}
          </>
        )}
      </button>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

const SendGiftPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  // Get recipient from route params, search params, or location state
  // Route: /send-gift/:recipientId passes data via params.recipientId
  // State: { recipient: { id, name, photo } } or { recipientId, recipientName, recipientPhoto }
  const recipientFromState = location.state?.recipient;
  const recipientIdFromParams = params.recipientId || searchParams.get('recipient') || recipientFromState?.id || location.state?.recipientId;
  
  // Recipient state (will be fetched if not in location state)
  const [recipientId, setRecipientId] = useState(recipientIdFromParams);
  const [recipientName, setRecipientName] = useState(recipientFromState?.name || location.state?.recipientName || 'Member');
  const [recipientPhoto, setRecipientPhoto] = useState(recipientFromState?.photo || recipientFromState?.photos?.[0] || location.state?.recipientPhoto);

  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Category, 2: Amount, 3: Details, 4: Review, 5: Payment, 6: Success
  
  // Form state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [amountCents, setAmountCents] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [privacyOption, setPrivacyOption] = useState('visible');
  const [occasion, setOccasion] = useState('');
  
  // Fee calculation
  const [feeInfo, setFeeInfo] = useState(null);
  const [loadingFee, setLoadingFee] = useState(false);
  
  // Payment state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [giftId, setGiftId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  
  // Config
  const [config, setConfig] = useState(null);

  // Theme detection
  const [isDark, setIsDark] = useState(true);
  
  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  // Fetch recipient details if not passed in state
  useEffect(() => {
    const fetchRecipient = async () => {
      if (recipientIdFromParams && recipientName === 'Member') {
        try {
          const res = await fetch(`${API_URL}/api/users/${recipientIdFromParams}`);
          if (res.ok) {
            const data = await res.json();
            if (data.name) setRecipientName(data.name);
            if (data.photo || data.photos?.[0]) {
              setRecipientPhoto(data.photo || data.photos?.[0]);
            }
          }
        } catch (err) {
          console.error('Failed to fetch recipient:', err);
        }
      }
    };
    fetchRecipient();
  }, [recipientIdFromParams]);

  // Load categories and config
  useEffect(() => {
    fetchCategories();
    fetchConfig();
  }, []);

  // Calculate fee when amount changes
  useEffect(() => {
    if (amountCents >= 100) {
      calculateFee();
    } else {
      setFeeInfo(null);
    }
  }, [amountCents]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gifts/categories`);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gifts/config`);
      const data = await res.json();
      setConfig(data);
      
      // Initialize Stripe with publishable key from config
      if (data.stripe_publishable_key) {
        const stripe = loadStripe(data.stripe_publishable_key);
        setStripePromise(stripe);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const calculateFee = async () => {
    if (amountCents < 100) return;
    
    setLoadingFee(true);
    try {
      const res = await fetch(`${API_URL}/api/gifts/calculate-fee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amountCents, membership_tier: 'standard' })
      });
      const data = await res.json();
      setFeeInfo(data);
    } catch (err) {
      console.error('Failed to calculate fee:', err);
    } finally {
      setLoadingFee(false);
    }
  };

  const handleCustomAmountChange = (value) => {
    // Remove non-numeric characters except decimal
    const cleaned = value.replace(/[^0-9.]/g, '');
    setCustomAmount(cleaned);
    
    // Convert to cents
    const dollars = parseFloat(cleaned);
    if (!isNaN(dollars) && dollars > 0) {
      const cents = Math.round(dollars * 100);
      setAmountCents(Math.min(cents, config?.max_single_gift_cents || 500000));
    } else {
      setAmountCents(0);
    }
  };

  const handlePresetAmount = (cents) => {
    setAmountCents(cents);
    setCustomAmount((cents / 100).toFixed(2));
  };

  const handleCreateGift = async () => {
    if (!recipientId || !selectedCategory || amountCents < 100) {
      setError('Please complete all required fields');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/gifts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: recipientId,
          category: selectedCategory.id,
          amount_cents: amountCents,
          message: message || null,
          privacy_option: privacyOption,
          occasion: occasion || null,
          funding_method: 'debit_card'
        })
      });

      const data = await res.json();

      if (data.success && data.client_secret) {
        setGiftId(data.gift_id);
        setClientSecret(data.client_secret);
        setCurrentStep(5); // Go to payment step
      } else if (data.success) {
        // Fallback if no client secret (mock mode)
        setGiftId(data.gift_id);
        setSuccess(true);
        setCurrentStep(6);
      } else {
        setError(data.detail || 'Failed to create gift');
      }
    } catch (err) {
      console.error('Gift creation error:', err);
      setError('Your gift could not be completed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      // Confirm payment on backend
      const res = await fetch(`${API_URL}/api/gifts/confirm/${giftId}?payment_intent_id=${paymentIntentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setCurrentStep(6);
      } else {
        setError(data.detail || 'Payment confirmation failed');
      }
    } catch (err) {
      // Even if confirm fails, the webhook will handle it
      setSuccess(true);
      setCurrentStep(6);
    }
  };

  const handlePaymentError = (errorMsg) => {
    setError(errorMsg);
  };

  // Theme colors
  const colors = {
    background: isDark ? '#0D0D0D' : '#FFFFFF',
    cardBg: isDark ? 'rgba(28, 28, 28, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    text: isDark ? '#F4E7C3' : '#1A1A1A',
    textMuted: isDark ? 'rgba(244, 231, 195, 0.7)' : 'rgba(26, 26, 26, 0.6)',
    border: isDark ? 'rgba(212, 175, 55, 0.3)' : 'rgba(165, 124, 51, 0.2)',
    accent: '#D4AF37',
    accentDark: '#A57C33'
  };

  const renderStepIndicator = () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      gap: '8px', 
      marginBottom: '24px',
      padding: '0 16px'
    }}>
      {[1, 2, 3, 4, 5].map(step => (
        <div
          key={step}
          style={{
            width: currentStep >= step ? '28px' : '12px',
            height: '4px',
            borderRadius: '2px',
            background: currentStep >= step 
              ? 'linear-gradient(90deg, #D4AF37, #A57C33)' 
              : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease'
          }}
        />
      ))}
    </div>
  );

  const renderCategoryStep = () => (
    <div style={{ padding: '0 16px' }}>
      <h2 style={{ 
        color: colors.text, 
        fontSize: '24px', 
        fontWeight: '600',
        marginBottom: '8px',
        fontFamily: 'Cinzel, serif'
      }}>
        Select Gift Type
      </h2>
      <p style={{ color: colors.textMuted, marginBottom: '24px', fontSize: '14px' }}>
        Choose a category for your gift to {recipientName}
      </p>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '12px' 
      }}>
        {categories.map(cat => {
          const Icon = CATEGORY_ICONS[cat.id] || Gift;
          const isSelected = selectedCategory?.id === cat.id;
          
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat.id}`}
              style={{
                padding: '16px',
                borderRadius: '16px',
                border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                background: isSelected 
                  ? `linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(212, 175, 55, 0.05))`
                  : colors.cardBg,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${cat.color || colors.accent}, ${colors.accentDark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}>
                <Icon size={24} color="#FFF" />
              </div>
              <div style={{ 
                color: colors.text, 
                fontWeight: '600', 
                fontSize: '14px',
                marginBottom: '4px'
              }}>
                {cat.name}
              </div>
              <div style={{ 
                color: colors.textMuted, 
                fontSize: '11px',
                lineHeight: '1.3'
              }}>
                {cat.description}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => selectedCategory && setCurrentStep(2)}
        disabled={!selectedCategory}
        data-testid="continue-to-amount"
        style={{
          width: '100%',
          padding: '16px',
          marginTop: '24px',
          borderRadius: '12px',
          border: 'none',
          background: selectedCategory 
            ? 'linear-gradient(135deg, #D4AF37, #A57C33)' 
            : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          color: selectedCategory ? '#1A1A1A' : colors.textMuted,
          fontSize: '16px',
          fontWeight: '600',
          cursor: selectedCategory ? 'pointer' : 'not-allowed',
          transition: 'all 0.3s ease'
        }}
      >
        Continue
      </button>
    </div>
  );

  const renderAmountStep = () => (
    <div style={{ padding: '0 16px' }}>
      <h2 style={{ 
        color: colors.text, 
        fontSize: '24px', 
        fontWeight: '600',
        marginBottom: '8px',
        fontFamily: 'Cinzel, serif'
      }}>
        Gift Amount
      </h2>
      <p style={{ color: colors.textMuted, marginBottom: '24px', fontSize: '14px' }}>
        {recipientName} receives the full amount you choose
      </p>

      {/* Preset amounts */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '8px',
        marginBottom: '16px'
      }}>
        {PRESET_AMOUNTS.map(preset => (
          <button
            key={preset.cents}
            onClick={() => handlePresetAmount(preset.cents)}
            data-testid={`amount-${preset.cents}`}
            style={{
              padding: '12px 8px',
              borderRadius: '10px',
              border: `2px solid ${amountCents === preset.cents ? colors.accent : colors.border}`,
              background: amountCents === preset.cents 
                ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(212, 175, 55, 0.05))'
                : 'transparent',
              color: amountCents === preset.cents ? colors.accent : colors.text,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Custom amount input */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        padding: '16px',
        border: `1px solid ${colors.border}`,
        marginBottom: '16px'
      }}>
        <label style={{ 
          color: colors.textMuted, 
          fontSize: '12px',
          display: 'block',
          marginBottom: '8px'
        }}>
          Or enter custom amount
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: colors.accent, fontSize: '24px', fontWeight: '600' }}>$</span>
          <input
            type="text"
            value={customAmount}
            onChange={(e) => handleCustomAmountChange(e.target.value)}
            placeholder="0.00"
            data-testid="custom-amount-input"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '32px',
              fontWeight: '600',
              color: colors.text,
              fontFamily: 'inherit'
            }}
          />
        </div>
        <div style={{ 
          color: colors.textMuted, 
          fontSize: '11px',
          marginTop: '8px'
        }}>
          Maximum ${((config?.max_single_gift_cents || 500000) / 100).toLocaleString()} per gift
        </div>
      </div>

      {/* Fee breakdown */}
      {feeInfo && (
        <div style={{
          background: isDark ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.05)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Gift Amount</span>
            <span style={{ color: colors.text, fontSize: '14px', fontWeight: '600' }}>
              {feeInfo.amount_display}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Secure Gift Delivery</span>
            <span style={{ color: colors.text, fontSize: '14px' }}>
              {feeInfo.fee_display}
            </span>
          </div>
          <div style={{ 
            borderTop: `1px solid ${colors.border}`, 
            paddingTop: '8px',
            marginTop: '8px',
            display: 'flex', 
            justifyContent: 'space-between'
          }}>
            <span style={{ color: colors.text, fontSize: '16px', fontWeight: '600' }}>Total</span>
            <span style={{ 
              color: colors.accent, 
              fontSize: '16px', 
              fontWeight: '700'
            }}>
              {feeInfo.total_display}
            </span>
          </div>
          <div style={{ 
            marginTop: '12px',
            padding: '8px 12px',
            background: isDark ? 'rgba(212, 175, 55, 0.1)' : 'rgba(212, 175, 55, 0.08)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Check size={16} color={colors.accent} />
            <span style={{ color: colors.accent, fontSize: '13px', fontWeight: '500' }}>
              {recipientName} receives {feeInfo.recipient_receives}
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={() => setCurrentStep(1)}
          style={{
            padding: '16px 24px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            background: 'transparent',
            color: colors.text,
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Back
        </button>
        <button
          onClick={() => amountCents >= 100 && setCurrentStep(3)}
          disabled={amountCents < 100}
          data-testid="continue-to-details"
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '12px',
            border: 'none',
            background: amountCents >= 100 
              ? 'linear-gradient(135deg, #D4AF37, #A57C33)' 
              : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            color: amountCents >= 100 ? '#1A1A1A' : colors.textMuted,
            fontSize: '16px',
            fontWeight: '600',
            cursor: amountCents >= 100 ? 'pointer' : 'not-allowed'
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div style={{ padding: '0 16px' }}>
      <h2 style={{ 
        color: colors.text, 
        fontSize: '24px', 
        fontWeight: '600',
        marginBottom: '24px',
        fontFamily: 'Cinzel, serif'
      }}>
        Personalize Your Gift
      </h2>

      {/* Message */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          color: colors.textMuted, 
          fontSize: '12px',
          display: 'block',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Personal Message (Optional)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write a personal message..."
          data-testid="gift-message"
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            background: colors.cardBg,
            color: colors.text,
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'none',
            height: '100px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          maxLength={500}
        />
        <div style={{ 
          color: colors.textMuted, 
          fontSize: '11px',
          textAlign: 'right',
          marginTop: '4px'
        }}>
          {message.length}/500
        </div>
      </div>

      {/* Privacy Options */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          color: colors.textMuted, 
          fontSize: '12px',
          display: 'block',
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Privacy Preference
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {PRIVACY_OPTIONS.map(option => {
            const Icon = option.icon;
            const isSelected = privacyOption === option.id;
            
            return (
              <button
                key={option.id}
                onClick={() => setPrivacyOption(option.id)}
                data-testid={`privacy-${option.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                  background: isSelected 
                    ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), transparent)'
                    : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: isSelected 
                    ? 'linear-gradient(135deg, #D4AF37, #A57C33)'
                    : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={18} color={isSelected ? '#FFF' : colors.textMuted} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    color: colors.text, 
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '2px'
                  }}>
                    {option.label}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                    {option.description}
                  </div>
                </div>
                {isSelected && (
                  <Check size={20} color={colors.accent} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={() => setCurrentStep(2)}
          style={{
            padding: '16px 24px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            background: 'transparent',
            color: colors.text,
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Back
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          data-testid="continue-to-review"
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
            color: '#1A1A1A',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Review Gift
        </button>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div style={{ padding: '0 16px' }}>
      <h2 style={{ 
        color: colors.text, 
        fontSize: '24px', 
        fontWeight: '600',
        marginBottom: '24px',
        fontFamily: 'Cinzel, serif'
      }}>
        Review Your Gift
      </h2>

      {/* Recipient Card */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        padding: '20px',
        border: `1px solid ${colors.border}`,
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: recipientPhoto 
              ? `url(${recipientPhoto}) center/cover`
              : 'linear-gradient(135deg, #D4AF37, #A57C33)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {!recipientPhoto && <User size={28} color="#FFF" />}
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>
              Sending to
            </div>
            <div style={{ color: colors.text, fontSize: '18px', fontWeight: '600' }}>
              {recipientName}
            </div>
          </div>
        </div>
      </div>

      {/* Gift Details */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        padding: '20px',
        border: `1px solid ${colors.border}`,
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>Gift Type</span>
          <span style={{ color: colors.text, fontSize: '14px', fontWeight: '500' }}>
            {selectedCategory?.name}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>Gift Amount</span>
          <span style={{ color: colors.text, fontSize: '14px', fontWeight: '500' }}>
            {feeInfo?.amount_display}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>Secure Delivery Fee</span>
          <span style={{ color: colors.text, fontSize: '14px' }}>
            {feeInfo?.fee_display}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>Privacy</span>
          <span style={{ color: colors.text, fontSize: '14px' }}>
            {PRIVACY_OPTIONS.find(p => p.id === privacyOption)?.label}
          </span>
        </div>
        {message && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>
              Your Message
            </div>
            <div style={{ 
              color: colors.text, 
              fontSize: '14px',
              fontStyle: 'italic',
              background: isDark ? 'rgba(212, 175, 55, 0.05)' : 'rgba(212, 175, 55, 0.03)',
              padding: '12px',
              borderRadius: '8px'
            }}>
              "{message}"
            </div>
          </div>
        )}
      </div>

      {/* Total */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(212, 175, 55, 0.05))',
        borderRadius: '16px',
        padding: '20px',
        border: `2px solid ${colors.accent}`,
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>
              Total to Pay
            </div>
            <div style={{ color: colors.accent, fontSize: '28px', fontWeight: '700' }}>
              {feeInfo?.total_display}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>
              {recipientName} receives
            </div>
            <div style={{ color: colors.text, fontSize: '18px', fontWeight: '600' }}>
              {feeInfo?.recipient_receives}
            </div>
          </div>
        </div>
      </div>

      {/* Legal Notice */}
      <div style={{
        padding: '12px 16px',
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <p style={{ 
          color: colors.textMuted, 
          fontSize: '11px',
          lineHeight: '1.5',
          margin: 0
        }}>
          By proceeding, you agree to our Gift Program Terms. Financial functionality is subject 
          to final sponsor-bank, issuing-program, regulatory and legal approval.
        </p>
      </div>

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <AlertCircle size={18} color="#DC3545" />
          <span style={{ color: '#DC3545', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => setCurrentStep(3)}
          disabled={processing}
          style={{
            padding: '16px 24px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            background: 'transparent',
            color: colors.text,
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            opacity: processing ? 0.5 : 1
          }}
        >
          Back
        </button>
        <button
          onClick={handleCreateGift}
          disabled={processing}
          data-testid="proceed-to-payment"
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
            color: '#1A1A1A',
            fontSize: '16px',
            fontWeight: '600',
            cursor: processing ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: processing ? 0.7 : 1
          }}
        >
          {processing ? (
            <>Processing...</>
          ) : (
            <>
              <CreditCard size={18} />
              Proceed to Payment
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderPaymentStep = () => (
    <div style={{ padding: '0 16px' }}>
      <h2 style={{ 
        color: colors.text, 
        fontSize: '24px', 
        fontWeight: '600',
        marginBottom: '8px',
        fontFamily: 'Cinzel, serif'
      }}>
        Payment
      </h2>
      <p style={{ color: colors.textMuted, marginBottom: '24px', fontSize: '14px' }}>
        Enter your payment details to complete the gift
      </p>

      {/* Order Summary */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(212, 175, 55, 0.03))',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '24px',
        border: `1px solid ${colors.accent}`
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>
            {selectedCategory?.name} Gift to {recipientName}
          </span>
          <span style={{ color: colors.text, fontSize: '14px', fontWeight: '500' }}>
            {feeInfo?.amount_display}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>Service Fee</span>
          <span style={{ color: colors.text, fontSize: '14px' }}>
            {feeInfo?.fee_display}
          </span>
        </div>
        <div style={{ 
          borderTop: `1px solid ${colors.border}`, 
          paddingTop: '8px',
          marginTop: '8px',
          display: 'flex', 
          justifyContent: 'space-between'
        }}>
          <span style={{ color: colors.text, fontSize: '16px', fontWeight: '600' }}>Total</span>
          <span style={{ color: colors.accent, fontSize: '16px', fontWeight: '700' }}>
            {feeInfo?.total_display}
          </span>
        </div>
      </div>

      {/* Stripe Payment Element */}
      {stripePromise && clientSecret ? (
        <Elements 
          stripe={stripePromise} 
          options={{
            clientSecret,
            appearance: {
              theme: isDark ? 'night' : 'stripe',
              variables: {
                colorPrimary: '#D4AF37',
                colorBackground: isDark ? '#1C1C1C' : '#FFFFFF',
                colorText: isDark ? '#F4E7C3' : '#1A1A1A',
                colorDanger: '#DC3545',
                fontFamily: 'system-ui, sans-serif',
                borderRadius: '8px',
              }
            }
          }}
        >
          <StripePaymentForm
            giftId={giftId}
            totalCents={feeInfo?.total_cents || 0}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            colors={colors}
            isDark={isDark}
          />
        </Elements>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: colors.textMuted
        }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
          <p>Loading payment form...</p>
        </div>
      )}

      <button
        onClick={() => setCurrentStep(4)}
        style={{
          width: '100%',
          padding: '14px',
          marginTop: '16px',
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          background: 'transparent',
          color: colors.textMuted,
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        Back to Review
      </button>

      {/* Security badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '24px',
        color: colors.textMuted,
        fontSize: '12px'
      }}>
        <Lock size={14} />
        <span>Secured by Stripe</span>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div style={{ 
      padding: '0 16px', 
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(212, 175, 55, 0.3)'
      }}>
        <Check size={40} color="#FFF" />
      </div>

      <h2 style={{ 
        color: colors.text, 
        fontSize: '28px', 
        fontWeight: '600',
        marginBottom: '12px',
        fontFamily: 'Cinzel, serif'
      }}>
        Gift Sent!
      </h2>

      <p style={{ 
        color: colors.textMuted, 
        fontSize: '16px',
        marginBottom: '32px',
        maxWidth: '280px'
      }}>
        Your {feeInfo?.amount_display} {selectedCategory?.name} gift has been sent to {recipientName}
      </p>

      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        padding: '20px',
        border: `1px solid ${colors.border}`,
        marginBottom: '24px',
        width: '100%',
        maxWidth: '320px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Send size={20} color={colors.accent} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: colors.textMuted, fontSize: '12px' }}>
              Receipt sent to your email
            </div>
            <div style={{ color: colors.text, fontSize: '14px', fontWeight: '500' }}>
              Gift ID: {giftId?.slice(0, 8)}...
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => navigate('/gift-wallet')}
        data-testid="view-gifts"
        style={{
          width: '100%',
          maxWidth: '320px',
          padding: '16px',
          borderRadius: '12px',
          border: 'none',
          background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
          color: '#1A1A1A',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '12px'
        }}
      >
        View My Gifts
      </button>

      <button
        onClick={() => navigate('/discover')}
        style={{
          width: '100%',
          maxWidth: '320px',
          padding: '16px',
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          background: 'transparent',
          color: colors.text,
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer'
        }}
      >
        Back to Explore
      </button>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.background,
      paddingBottom: '100px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        borderBottom: `1px solid ${colors.border}`
      }}>
        <button
          onClick={() => {
            if (currentStep === 1) {
              navigate(-1);
            } else if (currentStep === 5) {
              setCurrentStep(4);
            } else if (currentStep === 6) {
              navigate('/gift-wallet');
            } else {
              setCurrentStep(currentStep - 1);
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowLeft size={24} color={colors.text} />
        </button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: colors.text,
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: 'Cinzel, serif'
        }}>
          {currentStep === 5 ? 'Payment' : currentStep === 6 ? 'Success' : 'Send Gift'}
        </h1>
        <div style={{ width: '40px' }} />
      </div>

      {/* Step Indicator */}
      {currentStep < 6 && renderStepIndicator()}

      {/* Content */}
      {currentStep === 1 && renderCategoryStep()}
      {currentStep === 2 && renderAmountStep()}
      {currentStep === 3 && renderDetailsStep()}
      {currentStep === 4 && renderReviewStep()}
      {currentStep === 5 && renderPaymentStep()}
      {currentStep === 6 && renderSuccessStep()}
    </div>
  );
};

export default SendGiftPage;
